import {
  AlertCircle,
  Book,
  BookOpen,
  CheckCircle,
  Edit2,
  FileText,
  HelpCircle,
  Layers,
  Link,
  Plus,
  RefreshCw,
  Save,
  Search,
  Settings,
  Target,
  Trash2,
  Unlink,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { authFetch as fetch } from '../services/api';
import QuestionManager from './QuestionManager';

// Types
interface Subject {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  exam_id: string;
  order: number;
  skills?: Skill[];
}

interface Skill {
  id: string;
  subject_id: string;
  module_name: string;
  skill_name: string;
  icon?: string;
  order: number;
  total_lessons: number;
  lessons?: LessonSummary[];
}

interface LessonSummary {
  id: string;
  name: string;
  description?: string;
  skill_id: string;
  order: number;
  question_count: number;
  prerequisite_id?: string;
  prerequisite_name?: string;
}

interface LessonDetail {
  id: string;
  name: string;
  description?: string;
  skill_id: string;
  order: number;
  question_count: number;
  prerequisite_lesson_id?: string;
  rule_content?: Record<string, unknown>;
  examples?: Array<Record<string, unknown>>;
  tips?: string;
  exercises?: Array<Record<string, unknown>>;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// API Functions
async function fetchSubjects(): Promise<Subject[]> {
  const response = await fetch(`${API_BASE_URL}/api/learning/admin/subjects`);
  if (!response.ok) throw new Error('Failed to fetch subjects');
  const data = await response.json();
  return data.subjects || [];
}

async function fetchSkillsBySubject(subjectId: string): Promise<Skill[]> {
  const response = await fetch(`${API_BASE_URL}/api/learning/admin/subjects/${subjectId}/skills`);
  if (!response.ok) throw new Error('Failed to fetch skills');
  const data = await response.json();
  return data.skills || [];
}

async function fetchLessonsBySkill(skillId: string): Promise<LessonSummary[]> {
  const response = await fetch(`${API_BASE_URL}/api/learning/admin/skills/${skillId}/lessons`);
  if (!response.ok) throw new Error('Failed to fetch lessons');
  const data = await response.json();
  return data.lessons || [];
}

async function fetchLessonDetail(lessonId: string): Promise<LessonDetail> {
  const response = await fetch(`${API_BASE_URL}/api/learning/admin/lessons/${lessonId}`);
  if (!response.ok) throw new Error('Failed to fetch lesson');
  const data = await response.json();
  return data.lesson;
}

async function updateLesson(lessonId: string, updates: Partial<LessonDetail>): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/learning/admin/lessons/${lessonId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error('Failed to update lesson');
}

async function deleteLesson(lessonId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/learning/admin/lessons/${lessonId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete lesson');
}

async function updateSubject(subjectId: string, updates: Partial<Subject>): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/learning/admin/subjects/${subjectId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error('Failed to update subject');
}

async function updateSkill(skillId: string, updates: Partial<Skill>): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/learning/admin/skills/${skillId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error('Failed to update skill');
}

async function createSubject(data: Partial<Subject>): Promise<Subject> {
  const response = await fetch(`${API_BASE_URL}/api/learning/admin/subjects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to create subject');
  return response.json();
}

async function createSkill(data: Partial<Skill>): Promise<Skill> {
  const response = await fetch(`${API_BASE_URL}/api/learning/admin/skills`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to create skill');
  return response.json();
}

async function setPrerequisite(lessonId: string, prerequisiteId: string | null): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/learning/admin/lessons/${lessonId}/prerequisite`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prerequisite_lesson_id: prerequisiteId }),
  });
  if (!response.ok) throw new Error('Failed to set prerequisite');
}

// Main Component
const LessonManager: React.FC = () => {
  // State
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [lessons, setLessons] = useState<LessonSummary[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<LessonDetail | null>(null);
  const [allLessons, setAllLessons] = useState<LessonSummary[]>([]); // For prerequisite selection

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Edit modes
  const [editingSubject, setEditingSubject] = useState<string | null>(null);
  const [editingSkill, setEditingSkill] = useState<string | null>(null);
  const [editingLesson, setEditingLesson] = useState<boolean>(false);

  // Form states
  const [subjectForm, setSubjectForm] = useState({ name: '', description: '', icon: '' });
  const [skillForm, setSkillForm] = useState({ module_name: '', skill_name: '', icon: '' });
  const [lessonForm, setLessonForm] = useState<Partial<LessonDetail>>({});

  // Modal states
  const [showCreateSubject, setShowCreateSubject] = useState(false);
  const [showCreateSkill, setShowCreateSkill] = useState(false);
  const [showPrerequisiteModal, setShowPrerequisiteModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{ type: string; id: string; name: string } | null>(null);
  const [showQuestionManager, setShowQuestionManager] = useState(false);

  // Load subjects on mount
  useEffect(() => {
    loadSubjects();
  }, []);

  const loadSubjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSubjects();
      setSubjects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadSkills = async (subjectId: string) => {
    setLoading(true);
    try {
      const data = await fetchSkillsBySubject(subjectId);
      setSkills(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadLessons = async (skillId: string) => {
    setLoading(true);
    try {
      const data = await fetchLessonsBySkill(skillId);
      setLessons(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadLessonDetail = async (lessonId: string) => {
    setLoading(true);
    try {
      const data = await fetchLessonDetail(lessonId);
      setSelectedLesson(data);
      setLessonForm(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadAllLessons = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/learning/admin/lessons`);
      if (response.ok) {
        const data = await response.json();
        setAllLessons(data.lessons || []);
      }
    } catch (err) {
      console.error('Failed to load all lessons', err);
    }
  };

  // Handlers
  const handleSelectSubject = (subject: Subject) => {
    setSelectedSubject(subject);
    setSelectedSkill(null);
    setSelectedLesson(null);
    setLessons([]);
    loadSkills(subject.id);
  };

  const handleSelectSkill = (skill: Skill) => {
    setSelectedSkill(skill);
    setSelectedLesson(null);
    loadLessons(skill.id);
  };

  const handleSelectLesson = (lesson: LessonSummary) => {
    loadLessonDetail(lesson.id);
    loadAllLessons();
  };

  const handleSaveSubject = async (subjectId: string) => {
    try {
      await updateSubject(subjectId, subjectForm);
      setSuccess('Sujet mis à jour');
      setEditingSubject(null);
      loadSubjects();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de mise à jour');
    }
  };

  const handleSaveSkill = async (skillId: string) => {
    try {
      await updateSkill(skillId, skillForm);
      setSuccess('Module mis à jour');
      setEditingSkill(null);
      if (selectedSubject) loadSkills(selectedSubject.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de mise à jour');
    }
  };

  const handleSaveLesson = async () => {
    if (!selectedLesson) return;
    try {
      await updateLesson(selectedLesson.id, lessonForm);
      setSuccess('Leçon mise à jour');
      setEditingLesson(false);
      loadLessonDetail(selectedLesson.id);
      if (selectedSkill) loadLessons(selectedSkill.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de mise à jour');
    }
  };

  const handleCreateSubject = async () => {
    try {
      await createSubject({
        ...subjectForm,
        exam_id: 'a1b2c3d4-e5f6-4a1b-8c9d-000000000001', // CAFOP exam ID
        order: subjects.length + 1,
      });
      setSuccess('Sujet créé');
      setShowCreateSubject(false);
      setSubjectForm({ name: '', description: '', icon: '' });
      loadSubjects();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de création');
    }
  };

  const handleCreateSkill = async () => {
    if (!selectedSubject) return;
    try {
      await createSkill({
        ...skillForm,
        subject_id: selectedSubject.id,
        order: skills.length + 1,
        total_lessons: 0,
      });
      setSuccess('Module créé');
      setShowCreateSkill(false);
      setSkillForm({ module_name: '', skill_name: '', icon: '' });
      loadSkills(selectedSubject.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de création');
    }
  };

  const handleSetPrerequisite = async (prerequisiteId: string | null) => {
    if (!selectedLesson) return;
    try {
      await setPrerequisite(selectedLesson.id, prerequisiteId);
      setSuccess('Prérequis mis à jour');
      setShowPrerequisiteModal(false);
      loadLessonDetail(selectedLesson.id);
      if (selectedSkill) loadLessons(selectedSkill.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de mise à jour');
    }
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) return;
    try {
      if (showDeleteConfirm.type === 'lesson') {
        await deleteLesson(showDeleteConfirm.id);
        setSelectedLesson(null);
        if (selectedSkill) loadLessons(selectedSkill.id);
      }
      setSuccess('Élément supprimé');
      setShowDeleteConfirm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de suppression');
    }
  };

  // Clear messages after 3 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Filter lessons by search
  const filteredLessons = lessons.filter(
    (l) =>
      l.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6 text-indigo-600" />
            <h1 className="text-xl font-semibold text-gray-900">
              Gestion des Leçons
            </h1>
          </div>
          <button
            onClick={loadSubjects}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
      </header>

      {/* Notifications */}
      {(success || error) && (
        <div className={`mx-6 mt-4 p-4 rounded-lg flex items-center gap-2 ${success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
          {success ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {success || error}
        </div>
      )}

      {/* Main Content */}
      <div className="flex h-[calc(100vh-80px)]">
        {/* Left Panel - Subjects */}
        <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-medium text-gray-700 flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Sujets
              </h2>
              <button
                onClick={() => {
                  setSubjectForm({ name: '', description: '', icon: '' });
                  setShowCreateSubject(true);
                }}
                className="p-1 hover:bg-gray-100 rounded"
                title="Nouveau sujet"
              >
                <Plus className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {subjects.map((subject) => (
              <div
                key={subject.id}
                className={`p-3 cursor-pointer transition ${selectedSubject?.id === subject.id
                    ? 'bg-indigo-50 border-l-4 border-indigo-500'
                    : 'hover:bg-gray-50'
                  }`}
                onClick={() => handleSelectSubject(subject)}
              >
                {editingSubject === subject.id ? (
                  <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={subjectForm.name}
                      onChange={(e) => setSubjectForm({ ...subjectForm, name: e.target.value })}
                      className="w-full px-2 py-1 text-sm border rounded"
                      placeholder="Nom"
                    />
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleSaveSubject(subject.id)}
                        className="flex-1 px-2 py-1 bg-indigo-600 text-white text-xs rounded"
                      >
                        Sauver
                      </button>
                      <button
                        onClick={() => setEditingSubject(null)}
                        className="px-2 py-1 bg-gray-300 text-xs rounded"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{subject.icon || '📚'}</span>
                      <span className="text-sm font-medium text-gray-800">{subject.name}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSubjectForm({ name: subject.name, description: subject.description || '', icon: subject.icon || '' });
                        setEditingSubject(subject.id);
                      }}
                      className="p-1 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100"
                    >
                      <Edit2 className="w-3 h-3 text-gray-500" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Middle Panel - Skills/Modules */}
        <div className="w-72 bg-gray-50 border-r border-gray-200 overflow-y-auto">
          {selectedSubject ? (
            <>
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-medium text-gray-700 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Modules - {selectedSubject.name}
                  </h2>
                  <button
                    onClick={() => {
                      setSkillForm({ module_name: '', skill_name: '', icon: '' });
                      setShowCreateSkill(true);
                    }}
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Nouveau module"
                  >
                    <Plus className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>

              <div className="divide-y divide-gray-200">
                {skills.map((skill) => (
                  <div
                    key={skill.id}
                    className={`p-3 cursor-pointer transition ${selectedSkill?.id === skill.id
                        ? 'bg-indigo-50 border-l-4 border-indigo-500'
                        : 'hover:bg-white'
                      }`}
                    onClick={() => handleSelectSkill(skill)}
                  >
                    {editingSkill === skill.id ? (
                      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          value={skillForm.module_name}
                          onChange={(e) => setSkillForm({ ...skillForm, module_name: e.target.value })}
                          className="w-full px-2 py-1 text-sm border rounded"
                          placeholder="Module"
                        />
                        <input
                          type="text"
                          value={skillForm.skill_name}
                          onChange={(e) => setSkillForm({ ...skillForm, skill_name: e.target.value })}
                          className="w-full px-2 py-1 text-sm border rounded"
                          placeholder="Compétence"
                        />
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleSaveSkill(skill.id)}
                            className="flex-1 px-2 py-1 bg-indigo-600 text-white text-xs rounded"
                          >
                            Sauver
                          </button>
                          <button
                            onClick={() => setEditingSkill(null)}
                            className="px-2 py-1 bg-gray-300 text-xs rounded"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-800">
                            {skill.module_name}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSkillForm({
                                module_name: skill.module_name,
                                skill_name: skill.skill_name,
                                icon: skill.icon || '',
                              });
                              setEditingSkill(skill.id);
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Edit2 className="w-3 h-3 text-gray-500" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">{skill.skill_name}</span>
                          <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">
                            {skill.total_lessons} leçons
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              <div className="text-center">
                <Layers className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Sélectionnez un sujet</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Lessons List & Detail */}
        <div className="flex-1 flex">
          {/* Lessons List */}
          <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
            {selectedSkill ? (
              <>
                <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                  <h2 className="font-medium text-gray-700 flex items-center gap-2 mb-3">
                    <BookOpen className="w-4 h-4" />
                    Leçons - {selectedSkill.skill_name}
                  </h2>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="divide-y divide-gray-100">
                  {filteredLessons.map((lesson, idx) => (
                    <div
                      key={lesson.id}
                      className={`p-3 cursor-pointer transition ${selectedLesson?.id === lesson.id
                          ? 'bg-indigo-50 border-l-4 border-indigo-500'
                          : 'hover:bg-gray-50'
                        }`}
                      onClick={() => handleSelectLesson(lesson)}
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {lesson.name}
                          </p>
                          {lesson.prerequisite_name && (
                            <p className="text-xs text-indigo-600 flex items-center gap-1 mt-1">
                              <Link className="w-3 h-3" />
                              Prérequis: {lesson.prerequisite_name}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-500">
                              {lesson.question_count} exercices
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <BookOpen className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Sélectionnez un module</p>
                </div>
              </div>
            )}
          </div>

          {/* Lesson Detail */}
          <div className="flex-1 bg-gray-50 overflow-y-auto">
            {selectedLesson ? (
              <div className="p-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  {/* Header */}
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-indigo-600" />
                      {selectedLesson.name}
                    </h2>
                    <div className="flex items-center gap-2">
                      {editingLesson ? (
                        <>
                          <button
                            onClick={handleSaveLesson}
                            className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
                          >
                            <Save className="w-4 h-4" />
                            Sauvegarder
                          </button>
                          <button
                            onClick={() => {
                              setEditingLesson(false);
                              setLessonForm(selectedLesson);
                            }}
                            className="px-3 py-1.5 bg-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-400"
                          >
                            Annuler
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setEditingLesson(true)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-indigo-100 text-indigo-700 text-sm rounded-lg hover:bg-indigo-200"
                          >
                            <Edit2 className="w-4 h-4" />
                            Modifier
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm({
                              type: 'lesson',
                              id: selectedLesson.id,
                              name: selectedLesson.name,
                            })}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200"
                          >
                            <Trash2 className="w-4 h-4" />
                            Supprimer
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nom de la leçon
                        </label>
                        {editingLesson ? (
                          <input
                            type="text"
                            value={lessonForm.name || ''}
                            onChange={(e) => setLessonForm({ ...lessonForm, name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                          />
                        ) : (
                          <p className="px-3 py-2 bg-gray-50 rounded-lg">{selectedLesson.name}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Ordre
                        </label>
                        {editingLesson ? (
                          <input
                            type="number"
                            value={lessonForm.order || 1}
                            onChange={(e) => setLessonForm({ ...lessonForm, order: parseInt(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                          />
                        ) : (
                          <p className="px-3 py-2 bg-gray-50 rounded-lg">{selectedLesson.order}</p>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      {editingLesson ? (
                        <textarea
                          value={lessonForm.description || ''}
                          onChange={(e) => setLessonForm({ ...lessonForm, description: e.target.value })}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                      ) : (
                        <p className="px-3 py-2 bg-gray-50 rounded-lg min-h-[60px]">
                          {selectedLesson.description || 'Aucune description'}
                        </p>
                      )}
                    </div>

                    {/* Prerequisite */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prérequis
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 px-3 py-2 bg-gray-50 rounded-lg flex items-center gap-2">
                          {selectedLesson.prerequisite_lesson_id ? (
                            <>
                              <Link className="w-4 h-4 text-indigo-600" />
                              <span>
                                {allLessons.find((l) => l.id === selectedLesson.prerequisite_lesson_id)?.name ||
                                  'Leçon liée'}
                              </span>
                            </>
                          ) : (
                            <>
                              <Unlink className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-500">Aucun prérequis</span>
                            </>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            loadAllLessons();
                            setShowPrerequisiteModal(true);
                          }}
                          className="px-3 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200"
                        >
                          {selectedLesson.prerequisite_lesson_id ? 'Changer' : 'Définir'}
                        </button>
                        {selectedLesson.prerequisite_lesson_id && (
                          <button
                            onClick={() => handleSetPrerequisite(null)}
                            className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                          >
                            Retirer
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div
                        className="bg-indigo-50 rounded-lg p-4 text-center cursor-pointer hover:bg-indigo-100 transition"
                        onClick={() => setShowQuestionManager(true)}
                      >
                        <HelpCircle className="w-6 h-6 text-indigo-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-indigo-700">
                          {selectedLesson.question_count}
                        </p>
                        <p className="text-xs text-indigo-600">Exercices</p>
                        <p className="text-xs text-indigo-500 mt-1">Cliquer pour gérer</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 text-center">
                        <Book className="w-6 h-6 text-green-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-green-700">
                          {selectedLesson.examples?.length || 0}
                        </p>
                        <p className="text-xs text-green-600">Exemples</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-4 text-center">
                        <Target className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-amber-700">
                          {selectedLesson.order}
                        </p>
                        <p className="text-xs text-amber-600">Position</p>
                      </div>
                    </div>

                    {/* Tips */}
                    {selectedLesson.tips && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Astuces
                        </label>
                        <div className="px-3 py-2 bg-amber-50 rounded-lg text-sm">
                          {selectedLesson.tips}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <FileText className="w-16 h-16 mx-auto mb-3 opacity-50" />
                  <p className="text-lg">Sélectionnez une leçon</p>
                  <p className="text-sm">pour voir les détails</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {/* Create Subject Modal */}
      {showCreateSubject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Nouveau Sujet</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={subjectForm.name}
                  onChange={(e) => setSubjectForm({ ...subjectForm, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Ex: Sciences"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={subjectForm.description}
                  onChange={(e) => setSubjectForm({ ...subjectForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Icône (emoji)</label>
                <input
                  type="text"
                  value={subjectForm.icon}
                  onChange={(e) => setSubjectForm({ ...subjectForm, icon: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="🔬"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowCreateSubject(false)}
                className="px-4 py-2 bg-gray-200 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateSubject}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Skill Modal */}
      {showCreateSkill && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Nouveau Module</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du Module</label>
                <input
                  type="text"
                  value={skillForm.module_name}
                  onChange={(e) => setSkillForm({ ...skillForm, module_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Ex: Orthographe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Compétence</label>
                <input
                  type="text"
                  value={skillForm.skill_name}
                  onChange={(e) => setSkillForm({ ...skillForm, skill_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Ex: Les Homophones"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Icône (emoji)</label>
                <input
                  type="text"
                  value={skillForm.icon}
                  onChange={(e) => setSkillForm({ ...skillForm, icon: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="📝"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowCreateSkill(false)}
                className="px-4 py-2 bg-gray-200 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateSkill}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prerequisite Modal */}
      {showPrerequisiteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Link className="w-5 h-5 text-indigo-600" />
              Définir le Prérequis
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Sélectionnez la leçon qui doit être complétée avant "{selectedLesson?.name}"
            </p>
            <div className="flex-1 overflow-y-auto border rounded-lg divide-y">
              {allLessons
                .filter((l) => l.id !== selectedLesson?.id)
                .map((lesson) => (
                  <div
                    key={lesson.id}
                    onClick={() => handleSetPrerequisite(lesson.id)}
                    className={`p-3 cursor-pointer hover:bg-indigo-50 transition ${selectedLesson?.prerequisite_lesson_id === lesson.id ? 'bg-indigo-100' : ''
                      }`}
                  >
                    <p className="font-medium text-gray-800">{lesson.name}</p>
                    <p className="text-xs text-gray-500">{lesson.question_count} exercices</p>
                  </div>
                ))}
            </div>
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
              <button
                onClick={() => setShowPrerequisiteModal(false)}
                className="px-4 py-2 bg-gray-200 rounded-lg"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Confirmer la suppression</h3>
                <p className="text-sm text-gray-500">Cette action est irréversible</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              Voulez-vous vraiment supprimer <strong>"{showDeleteConfirm.name}"</strong> ?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 bg-gray-200 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Question Manager Modal */}
      {showQuestionManager && selectedLesson && (
        <QuestionManager
          lessonId={selectedLesson.id}
          lessonName={selectedLesson.name}
          onClose={() => {
            setShowQuestionManager(false);
            // Reload lesson detail to update question count
            loadLessonDetail(selectedLesson.id);
          }}
        />
      )}
    </div>
  );
};

export default LessonManager;
